import { useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Building, Clock, Image, MapPin } from 'lucide-react';
import { useBoolean } from 'usehooks-ts';

import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE,
} from '@/api/fe-api/domain/entities/site';
import { SiteOperationalPhase } from '@/api/fe-api/domain/entities/site-operational-phase';
import { UpdateSite } from '@/api/fe-api/view/mutations/update-site';
import { DatePicker } from '@/components/form/inputs/date-picker';
import { NumberInput } from '@/components/form/inputs/number-input';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import { useMe } from '@/hooks/use-me';
import { FileUploadBox } from '@/modules/documents/view/components/pages/site-documents/file-upload-box';
import { formStyle } from '@/styles/form-style';
import { cls } from '@/utils/cls';
import { formatMB } from '@/utils/format/bytes';
import { toastResult } from '@/utils/toast';

import { EnergySuffix } from './energy-suffix';
import { PowerSuffix } from './power-suffix';
import { type FormUpdateInputs, formUpdateSchema } from './types';

export type FormEditAssetProps = {
  onSuccess: () => void;
  onConfirmCancel: (isDirty: boolean) => void;
  onChange: ({ isDirty }: { isDirty: boolean }) => void;
};

export function FormEditAsset({
  onSuccess,
  onChange,
  onConfirmCancel,
}: FormEditAssetProps) {
  const { asset } = useCurrentAsset();
  const updateSite = UpdateSite.useMutation();
  const style = formStyle();
  const me = useMe();
  const showFileUpload = useBoolean(!asset?.pictureUrl);

  const initialValuesRef = useRef<Partial<FormUpdateInputs>>({});

  const form = useForm<FormUpdateInputs>({
    resolver: zodResolver(formUpdateSchema),
  });

  const { isDirty, isValid } = form.formState;

  const resetForm = useCallback(() => {
    if (asset) {
      const initialValues: Partial<FormUpdateInputs> = {
        operationalPhase: asset.operationalPhase,
        address: asset.address,
        latitude: asset.latitude,
        longitude: asset.longitude,
        nominalEnergyHumanReadable: asset.nominalEnergyWH
          ? asset.nominalEnergyWH / 10 ** me.config.powerUnit
          : undefined,
        nominalPowerHumanReadable: asset.nominalPowerW
          ? asset.nominalPowerW / 10 ** me.config.powerUnit
          : undefined,
        installationDate: asset.installationDate
          ? new Date(asset.installationDate)
          : undefined,
        commissioningDate: asset.commissioningDate
          ? new Date(asset.commissioningDate)
          : undefined,
      };

      // Store initial values for later comparison
      initialValuesRef.current = initialValues;

      form.reset(initialValues);
    }
  }, [asset, form, me.config.powerUnit]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  useEffect(() => onChange({ isDirty }), [onChange, isDirty]);

  if (!asset) {
    return null;
  }

  async function handleSubmit(data: FormUpdateInputs) {
    if (!asset) {
      return;
    }

    const result = updateSite.mutateAsync({
      id: asset.id,
      latitude: data.latitude,
      longitude: data.longitude,
      address: data.address,
      operationalPhase: data.operationalPhase,
      installationDate: data.installationDate,
      commissioningDate: data.commissioningDate,
      picture: data.picture,
      nominalEnergyWh: data.nominalEnergyHumanReadable
        ? data.nominalEnergyHumanReadable * 10 ** me.config.powerUnit
        : undefined,
      nominalPowerW: data.nominalPowerHumanReadable
        ? data.nominalPowerHumanReadable * 10 ** me.config.powerUnit
        : undefined,
    });

    await toastResult(result, {
      success: `Asset saved successfully`,
      loading: 'Saving asset...',
      error: 'Failed to save changes',
      exception: 'Failed to save changes',
    });

    if ((await result).isOk()) {
      onSuccess();
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="space-y-10">
          <div className={style.inputSection()}>
            <h3 className={style.inputSectionTitle()}>
              <Building className="text-neutral-400" />
              Basic Information
            </h3>
            <FormField
              control={form.control}
              name="operationalPhase"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Operational Phase</FormLabel>

                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="In Operation" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectGroup>
                        {Object.values(SiteOperationalPhase)
                          .filter(
                            (v) => v !== SiteOperationalPhase.MONITORING_ENDED,
                          )
                          .map((v) => (
                            <SelectItem key={v} value={v.toString()}>
                              {v.toString()}
                            </SelectItem>
                          ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className={style.inputSectionRow()}>
              <FormField
                control={form.control}
                name="nominalEnergyHumanReadable"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Energy</FormLabel>
                    <FormControl>
                      <NumberInput
                        {...field}
                        placeholder="100"
                        rightIcon={<EnergySuffix />}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="nominalPowerHumanReadable"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Power</FormLabel>
                    <FormControl>
                      <NumberInput
                        {...field}
                        placeholder="100"
                        rightIcon={<PowerSuffix />}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className={style.inputSection()}>
            <h3 className={style.inputSectionTitle()}>
              <MapPin className="text-neutral-400" />
              Location Details
            </h3>
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Av. Gustave Eiffel, 75007 Paris, France"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className={style.inputSectionRow()}>
              <FormField
                control={form.control}
                name="latitude"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Latitude</FormLabel>
                    <FormControl>
                      <NumberInput
                        {...field}
                        placeholder="50.7727581"
                        step="any"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="longitude"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Longitude</FormLabel>
                    <FormControl>
                      <NumberInput
                        {...field}
                        placeholder="6.1102437"
                        step="any"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className={style.inputSection()}>
            <h3 className={style.inputSectionTitle()}>
              <Clock className="text-neutral-400" />
              Project Timeline
            </h3>
            <div className={style.inputSectionRow()}>
              <FormField
                control={form.control}
                name="installationDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Installation Date</FormLabel>
                    <FormControl>
                      <DatePicker {...field} onSelect={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="commissioningDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Commissioning Date</FormLabel>
                    <FormControl>
                      <DatePicker {...field} onSelect={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className={style.inputSection()}>
            <h3 className={style.inputSectionTitle()}>
              <Image className="text-neutral-400" />
              Extras
            </h3>
            <FormField
              control={form.control}
              name="picture"
              render={({ field: { value, ...field }, fieldState }) => (
                <FormItem>
                  <FormLabel>
                    Picture <small className="text-gray-400">Optional</small>
                  </FormLabel>
                  <FormControl>
                    <>
                      {asset.pictureUrl ? (
                        <div
                          className={cls('group relative cursor-pointer', {
                            hidden: showFileUpload.value,
                          })}
                          onClick={showFileUpload.setTrue}
                        >
                          <img
                            src={asset.pictureUrl}
                            alt=""
                            className="size-full object-cover"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                            <span className="font-medium text-white">
                              Replace image
                            </span>
                          </div>
                        </div>
                      ) : null}
                      <FileUploadBox
                        {...field}
                        className={cls('hidden', {
                          block: showFileUpload.value,
                        })}
                        id="picture"
                        fileName={value?.name}
                        onChange={function (file) {
                          field.onChange(file ?? undefined);
                        }}
                        accept={ALLOWED_FILE_TYPES.join()}
                        error={Boolean(fieldState.error)}
                        hint={
                          fieldState.error?.message ??
                          `Maximum file size: ${formatMB(MAX_FILE_SIZE)}`
                        }
                      />
                    </>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div>
            <Button
              type="button"
              variant="outline"
              className={style.button()}
              onClick={() => onConfirmCancel(isDirty)}
            >
              Cancel
            </Button>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              className={style.button()}
              disabled={!isDirty}
              onClick={resetForm}
            >
              Discard changes
            </Button>
            <Button
              type="submit"
              className={style.button()}
              disabled={!isDirty || !isValid}
            >
              Save changes
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}
